import { forwardRef, type MouseEvent as ReactMouseEvent } from 'react';

import * as styles from './IntentButton.sty.js';
import { useIntentWithResponse } from '../../core/hooks/intent.js';
import { type IntentWithResponseProps } from '../../core/slots/Intent/Intent.js';
import { AppIcon } from '../../layouts/app-header/app-icon/AppIcon.js';
import { Button, ButtonOwnProps } from '../button/Button.js';

//#region Typings
/** Accepted properties for the IntentButtonWithResponse. */
type IntentButtonWithResponseProps = Omit<IntentWithResponseProps, 'onSelect'> &
  Pick<
    ButtonOwnProps,
    | 'onClick'
    | 'color'
    | 'variant'
    | 'width'
    | 'aria-disabled'
    | 'textAlign'
    | 'size'
  > & {
    disabled?: boolean;
  };
//#endregion

/**
 * Renders a button that sends an intent and handles the response.
 * Used when `responseProperties` and `onResponse` are provided.
 * @internal
 */
export const IntentButtonWithResponse = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  IntentButtonWithResponseProps
>((props, ref) => {
  const {
    onResponse,
    payload,
    options,
    children,
    icon,
    disabled,
    onClick,
    size,
    textAlign,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  } = props;

  const intentWithResponse = useIntentWithResponse({
    payload,
    options,
  });

  const handleClick = async (event: ReactMouseEvent) => {
    onClick?.(event);
    if (event.isDefaultPrevented()) {
      // already handled
      return;
    }

    try {
      const response = await intentWithResponse.send();
      onResponse(response);
    } catch (error) {
      onResponse({
        status: 'error',
        error: new Error('Error sending intent with response', {
          cause: error,
        }),
      });
    }
  };

  return (
    <Button
      ref={ref}
      onClick={(event) => void handleClick(event)}
      onMouseDown={(event: ReactMouseEvent) => {
        // allow middle mouse button click.
        if (event.nativeEvent.button === 1) {
          void handleClick(event);
        }
      }}
      disabled={disabled}
      aria-disabled={ariaDisabledProp}
      textAlign={textAlign}
      size={size}
      {...remainingProps}
    >
      {intentWithResponse.iconURL && (
        <Button.Prefix>
          <AppIcon
            src={intentWithResponse.iconURL}
            className={
              disabled || ariaDisabledProp
                ? styles.disabledAppIconOpacity
                : undefined
            }
            alt=""
          />
        </Button.Prefix>
      )}
      {icon && <Button.Prefix>{icon}</Button.Prefix>}
      {children}
    </Button>
  );
});
